import React, { useCallback } from 'react';

import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';

import {
    Button,
    DisclaimerText,
    Image,
    Link,
    List,
    Section,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { inventoryProtectionViewMoreClick } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import { ContentPreview, InfoIcon } from 'reaxl-molecules';
import { DigitalRetailOverlayCTA } from 'reaxl-wallet';

import Card from './Card';
import CardContentWithAnalytics from './CardContentWithAnalytics';
import { cardHeaders, cardImages, protectedItem, protectionDisclaimer } from './staticContent';

function AdditionalProtectionCard({
    digitalRetailProps,
    emailCTAProps,
    handleContentImpression = () => { },
    handleVehicleProtectionImpression = () => {},
    hideEmailForm,
    listingId,
    listingType,
    onEmailCTAClick,
    setModalDetail,
    ...rest
}) {

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const { brands, isBrand } = useBrand();
    const isAtc = isBrand(brands.ATC_BRAND);

    const handleViewInformationClick = useCallback((event) => {
        const handleEmailCTAclick = () => {
            onEmailCTAClick('additionalProtections');
            setModalDetail({ isShow: false });
        };

        const handleReadMoreClick = (thisEvent, title) => {
            sendClick('inventoryProtectionReadMoreClick', thisEvent, { title, listingType });
        };

        const emailCTA = {
            ...emailCTAProps,
            onClick: handleEmailCTAclick,
            labelKey: 'contactDealer',
            context: 'protection',
            showIcon: false,
        };

        import(/* webpackChunkName: "additionalProtection" */ '@/config/additionalProtection').then((data) => {
            const { additionalProtectionHeader, additionalProtectionFooter, additionalProtection } = data;

            const additionalProtectionItems = Object.keys(additionalProtection);
            setModalDetail({
                title: 'Optional Add-On Protections',
                isShow: true,
                content: (
                    <Section
                        alignment="left"
                        uiContext="level-3"
                        subheading={additionalProtectionHeader}
                        sectionBodyClassName="margin-top-3"
                    >
                        {additionalProtectionItems.length > 0 && additionalProtectionItems.map((item, index) => {
                            const { image, heading, body, href } = additionalProtection[item];
                            return (
                                <ContentPreview
                                    key={index}
                                    image={(
                                        <Image
                                            className="margin-right-sm-3"
                                            src={isAtc ? image.srcAtc : image.src}
                                            width={90}
                                            height={125}
                                            aria-hidden="true"
                                            alt=""
                                        />
                                    )}
                                    mediaClassName="margin-auto"
                                    contentClassName="margin-vertical-sm-5 margin-right-1"
                                    headline={(
                                        <Text
                                            weight="bold"
                                            componentClass="div"
                                            className="text-center text-sm-left"
                                        >
                                            {heading}
                                        </Text>
                                    )}
                                    summary={body}
                                    orientation={isXs ? 'portrait' : 'landscape'}
                                    cta={isAtc ? (
                                        <Link
                                            href={href}
                                            label="Read More"
                                            onClick={(thisEvent) => handleReadMoreClick(thisEvent, heading)}
                                            target="_blank"
                                        />
                                    ) : undefined}
                                />
                            );
                        })}
                        <DisclaimerText className="margin-vertical-4">{additionalProtectionFooter}</DisclaimerText>
                    </Section>
                ),
                footer: (
                    <div className="display-flex justify-content-end">
                        <Button
                            bsStyle="link"
                            onClick={() => setModalDetail({ isShow: false })}
                        >
                            Cancel
                        </Button>
                        {!hideEmailForm && <EmailCTA {...emailCTA} />}
                    </div>
                ),
                action: isXs && !hideEmailForm ? (
                    <EmailCTA
                        {...emailCTA}
                        buttonSize="small"
                    />
                ) : false,
                size: 'md',
            });
        });

        sendClick(inventoryProtectionViewMoreClick, event, { listingType, inventoryId: listingId });
    }, [isAtc, isXs, setModalDetail, emailCTAProps, listingType, onEmailCTAClick, hideEmailForm, listingId]);

    return (
        <Card
            id="protections"
            heading={(
                <React.Fragment key="additional-protections-heading">
                    {cardHeaders.ADDITIONAL_PROTECTIONS}
                    <InfoIcon
                        className="margin-left-1"
                        infoText={(<DisclaimerText>{protectionDisclaimer}</DisclaimerText>)}
                        placement="top"
                        popoverId="additional-protection-disclaimer"
                    />
                </React.Fragment>
            )}
            image={isAtc ? cardImages.PROTECTION_ATC : cardImages.PROTECTION}
            showTile
            {...rest}
        >
            <CardContentWithAnalytics
                cardType="additionalProtections"
                handleContentImpression={handleContentImpression}
                listingId={listingId}
            >
                <List
                    className="margin-vertical-1"
                    items={protectedItem}
                />
                {!(digitalRetailProps) && (
                    <Button
                        bsStyle="text-decorated"
                        className="margin-left-1"
                        onClick={handleViewInformationClick}
                    >
                        View more information
                    </Button>
                )}
            </CardContentWithAnalytics>
            {!!digitalRetailProps && (
                <CardContentWithAnalytics
                    cardType="vehicleProtection"
                    handleContentImpression={handleVehicleProtectionImpression}
                    listingId={listingId}
                >
                    <DigitalRetailOverlayCTA
                        {...digitalRetailProps}
                        uiContext="protectionList"
                    />
                </CardContentWithAnalytics>
            )}
        </Card>
    );
}

export default AdditionalProtectionCard;
